/**
 * ActiveSessions — workspace-wide list of running sessions
 * (Phase 12 / M10, founder mandate).
 *
 * Sorted by RAM desc so the heaviest session is on top. The Pause
 * action goes through chaos-session-runtime via the existing
 * /api/session/:id/sleep proxy (kept inline here to keep the widget
 * self-contained — the dashboard route can intercept if it wants).
 *
 * Refresh cadence: 10s (TanStack Query refetchInterval).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause } from 'lucide-react';

import { apiJson } from '../../lib/api/client';
import { systemApi, systemKeys } from '../../lib/api/system';
import { cn } from '../../lib/utils';

export interface ActiveSessionsProps {
  onOpenCard?: ((cardId: string, boardId?: string) => void) | undefined;
  className?: string | undefined;
}

export function ActiveSessions({ onOpenCard, className }: ActiveSessionsProps) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: systemKeys.activeSessions(),
    queryFn: () => systemApi.activeSessions(),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const pause = useMutation({
    mutationFn: (sessionId: string) => apiJson(`/session/${sessionId}/sleep`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: systemKeys.activeSessions() });
    },
  });

  const rows = q.data?.sessions ?? [];

  return (
    <section
      aria-labelledby="active-sessions-heading"
      className={cn('rounded-md border border-border bg-background p-4', className)}
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 id="active-sessions-heading" className="text-sm font-semibold text-foreground">
          Active sessions
        </h2>
        <span className="text-2xs text-muted-foreground">{rows.length} running</span>
      </header>
      {q.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded border border-dashed border-border bg-foreground/[0.02] p-4 text-center text-xs text-muted-foreground">
          No active sessions.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-2xs uppercase tracking-wide text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-2 py-1.5 text-left font-medium">Card</th>
                <th className="px-2 py-1.5 text-left font-medium">Model</th>
                <th className="px-2 py-1.5 text-right font-medium">Uptime</th>
                <th className="px-2 py-1.5 text-right font-medium">RAM</th>
                <th className="px-2 py-1.5 text-right font-medium">CPU</th>
                <th className="px-2 py-1.5 text-right font-medium">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.sessionId}
                  className="border-b border-border/60 hover:bg-foreground/[0.03]"
                >
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => onOpenCard?.(r.cardId, r.boardId)}
                      className="truncate text-left text-foreground transition-colors hover:underline"
                    >
                      {r.cardTitle}
                    </button>
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {r.model.length > 24 ? `${r.model.slice(0, 24)}…` : r.model}
                  </td>
                  <td className="px-2 py-1.5 text-right text-muted-foreground">
                    {formatUptime(r.uptimeSeconds)}
                  </td>
                  <td className="px-2 py-1.5 text-right text-foreground">
                    {r.ramMb !== null ? `${r.ramMb} MB` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right text-foreground">
                    {r.cpuPct !== null ? `${r.cpuPct.toFixed(0)}%` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => pause.mutate(r.sessionId)}
                      disabled={pause.isPending}
                      className="inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 text-2xs text-foreground transition-colors hover:bg-foreground/[0.04] disabled:opacity-50"
                      aria-label={`Pause session for ${r.cardTitle}`}
                    >
                      <Pause size={10} aria-hidden />
                      Pause
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
